import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { ScrollText, RefreshCw } from "lucide-react";
import { Button } from "../../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const eventTypeLabels = {
  user_registered: "Ny användare",
  user_login: "Inloggning",
  superadmin_login: "Admin inloggning",
  organization_created: "Ny organisation",
  organization_updated: "Organisation uppdaterad",
  incident_created: "Avvikelse rapporterad",
  user_created: "Användare skapad",
  user_updated: "Användare uppdaterad",
  user_deactivated: "Användare inaktiverad",
};

const eventTypeColors = {
  user_registered: "bg-green-500/20 text-green-400",
  user_login: "bg-blue-500/20 text-blue-400",
  superadmin_login: "bg-amber-500/20 text-amber-400",
  organization_created: "bg-blue-500/20 text-blue-400",
  organization_updated: "bg-cyan-500/20 text-cyan-400",
  incident_created: "bg-red-500/20 text-red-400",
  user_created: "bg-green-500/20 text-green-400",
  user_updated: "bg-cyan-500/20 text-cyan-400",
  user_deactivated: "bg-red-500/20 text-red-400",
};

export const AdminLogsPage = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState("all");

  useEffect(() => { fetchData(); }, []);

  const getToken = () => localStorage.getItem("admin_token");

  const fetchData = async () => {
    const token = getToken();
    if (!token) { navigate("/admin/login"); return; }
    setLoading(true);
    try {
      const [logsRes, typesRes] = await Promise.all([
        fetch(`${API}/admin/logs?limit=200`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/logs/event-types`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (logsRes.ok) setLogs(await logsRes.json());
      if (typesRes.ok) setEventTypes(await typesRes.json());
    } catch (err) {
      console.error("Failed to fetch logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = selectedType === "all"
    ? logs
    : logs.filter(l => l.event_type === selectedType);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }} data-testid="admin-logs-title">
            Systemlogg
          </h1>
          <p className="text-slate-400 mt-1">{filteredLogs.length} händelser</p>
        </div>
        <div className="flex gap-3">
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger className="w-[220px] bg-slate-800 border-slate-700 text-white" data-testid="admin-logs-type-filter">
              <SelectValue placeholder="Alla händelser" />
            </SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              <SelectItem value="all" className="text-white">Alla händelser</SelectItem>
              {eventTypes.map((type) => (
                <SelectItem key={type} value={type} className="text-white">
                  {eventTypeLabels[type] || type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="border-slate-700 text-slate-300" onClick={fetchData} data-testid="admin-logs-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          {filteredLogs.length === 0 ? (
            <div className="py-12 text-center">
              <ScrollText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">Inga loggposter hittades</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-700/50">
              {filteredLogs.map((log, index) => (
                <div key={log.log_id || index} className="p-4 hover:bg-slate-700/20 transition-colors" data-testid={`admin-log-entry-${index}`}>
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      <Badge className={eventTypeColors[log.event_type] || "bg-slate-500/20 text-slate-400"}>
                        {eventTypeLabels[log.event_type] || log.event_type}
                      </Badge>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-300">{log.description}</p>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="text-xs text-slate-500">
                          {new Date(log.created_at).toLocaleString('sv-SE')}
                        </span>
                        {log.user_email && (
                          <span className="text-xs text-slate-500">{log.user_email}</span>
                        )}
                        {log.organization_id && (
                          <span className="text-xs text-slate-600">Org: {log.organization_id}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
